import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Empty, Input, Modal, Tag, Tooltip, message } from 'antd';
import { CheckOutlined, DeleteOutlined, DownOutlined, EditOutlined, PlusOutlined, CopyOutlined, ReloadOutlined, SearchOutlined, AppstoreOutlined, FormOutlined } from '@ant-design/icons';
import { GripVertical, Maximize2, X } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { DndContext, DragOverlay, PointerSensor, KeyboardSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { CATEGORIES, TEMPLATES, WIDGETS, WIDGET_BY_TYPE, availableTemplates, makeWidget, newId, templateWidgets } from './catalog';

const MAX_DASHBOARDS = 20;
const MAX_WIDGETS = 60;
const GRID_DROP = 'dw-grid-drop';
const PLACEHOLDER = 'dw-placeholder';
// a press on these never starts a drag (period badges, inputs, buttons inside a widget)
const NO_DRAG = 'input, textarea, button, a, .no-drag, .ant-select, .ant-picker, .ant-dropdown, [role="menuitem"]';

const ModalShell = ({ open, close, title, children, footer, width = 560 }) => (
  <Modal open={open} width={width} footer={footer} closable={false} onCancel={close} destroyOnClose>
    <div className="modal-header-title">
      <span>{title}</span>
      <span onClick={close}><FontAwesomeIcon className="close" icon={faClose} /></span>
    </div>
    <div className="modal-body" style={{ margin: '16px 0 0' }}>{children}</div>
  </Modal>
);

const ModalFooter = ({ okText, onOk, disabled, close }) => (
  <div className="drawer-footer" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
    <button className={disabled ? 'disabled-btn drawer-filled-btn' : 'drawer-filled-btn'} disabled={disabled} onClick={onOk}>{okText}</button>
    <button className="drawer-outlined-btn" onClick={close}>Cancel</button>
  </div>
);

/** Columns × rows picker: hover the grid, click to set (cells outside what the widget can take are off) */
const SizePicker = ({ widget, fit, onPick }) => {
  const [hover, setHover] = useState(null);
  const show = hover || { w: widget.w, h: widget.h };
  return (
    <div className="dw-size-picker" onMouseLeave={() => setHover(null)}>
      <div className="dw-size-title">Size: {show.w} × {show.h}</div>
      <div className="dw-size-grid">
        {[1, 2, 3, 4].map((h) => [1, 2, 3, 4].map((w) => {
          const allowed = w >= fit.minW && w <= fit.maxW && h >= fit.minH && h <= fit.maxH;
          const on = w <= show.w && h <= show.h;
          return (
            <button
              key={`${w}x${h}`}
              type="button"
              data-size={`${w}x${h}`}
              disabled={!allowed}
              className={`dw-size-cell${on ? ' on' : ''}${allowed ? '' : ' off'}`}
              onMouseEnter={() => allowed && setHover({ w, h })}
              onClick={() => allowed && onPick({ w, h })}
              aria-label={`${w} wide, ${h} high`}
            />
          );
        }))}
      </div>
      <div className="dw-size-hint">columns across × rows down · or drag the widget's edge</div>
    </div>
  );
};

/**
 * Stretch / squeeze by dragging a widget's right edge, bottom edge or corner. The size snaps to
 * whole columns and rows of the grid as you go and is saved when you let go.
 */
const useStretch = ({ widget, fit, onPreview, onResize }) => (axis) => (event) => {
  event.preventDefault();
  event.stopPropagation();
  const grid = event.currentTarget.closest('.dw-grid');
  if (!grid) return;
  const style = getComputedStyle(grid);
  const cols = style.gridTemplateColumns.split(' ').filter(Boolean).length;
  const gap = parseFloat(style.columnGap) || 0;
  const rowGap = parseFloat(style.rowGap) || gap;
  const colStep = (grid.clientWidth - gap * (cols - 1)) / cols + gap;
  const rowStep = (parseFloat(style.gridAutoRows) || 132) + rowGap;
  const start = { x: event.clientX, y: event.clientY, w: Math.min(widget.w, cols), h: widget.h };
  const maxW = Math.min(fit.maxW, cols);
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  let size = { w: widget.w, h: widget.h };

  const move = (e) => {
    const w = axis.includes('e') ? clamp(start.w + Math.round((e.clientX - start.x) / colStep), Math.min(fit.minW, maxW), maxW) : widget.w;
    const h = axis.includes('s') ? clamp(start.h + Math.round((e.clientY - start.y) / rowStep), fit.minH, fit.maxH) : widget.h;
    if (w !== size.w || h !== size.h) { size = { w, h }; onPreview(size); }
  };
  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    document.body.classList.remove('dw-stretching');
    onPreview(null);
    if (size.w !== widget.w || size.h !== widget.h) onResize(size);
  };
  document.body.classList.add('dw-stretching');
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
};

const WidgetCell = ({ widget, def, editing, onRemove, onResize, readOnly, children }) => {
  // Moved like a widget from the panel: the cell stays put (dimmed) while a small card follows the
  // pointer and a box shows where it will land - nothing is squeezed or stretched mid-drag. A
  // standard (read-only) dashboard's widgets don't move.
  const drag = useDraggable({ id: widget.id, data: { widgetId: widget.id }, disabled: !!readOnly });
  const drop = useDroppable({ id: widget.id, disabled: !!readOnly });
  const { attributes, listeners, isDragging } = drag;
  const setNodeRef = (node) => { drag.setNodeRef(node); drop.setNodeRef(node); };
  const [sizeOpen, setSizeOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const stretch = useStretch({ widget, fit: def?.fit || { minW: 1, maxW: 4, minH: 1, maxH: 4 }, onPreview: setPreview, onResize });
  const size = preview || widget;
  // a drag starts anywhere on the widget except its own controls
  const onPointerDown = (event) => {
    if (event.target.closest?.(NO_DRAG) && !event.target.closest?.('.dw-grip')) return;
    listeners?.onPointerDown?.(event);
  };
  return (
    <div
      ref={setNodeRef}
      className={`dw-cell dw-w-${size.w} dw-h-${size.h}${isDragging ? ' dw-moving' : ''}${def ? '' : ' dw-unavailable'}${preview ? ' dw-resizing' : ''}`}
      data-widget={widget.type}
      data-widget-id={widget.id}
      {...attributes}
      {...listeners}
      onPointerDown={onPointerDown}
    >
      {def ? children : (
        <div className="dw-missing">
          <b>Not available</b>
          <span>This widget's module is off for your company, or your role can't open it.</span>
        </div>
      )}
      {editing && (
        <>
          <div className="dw-shield" />
          <div className="dw-tools no-drag" onPointerDown={(e) => e.stopPropagation()}>
            <span className="dw-tool dw-grip" title="Drag to move" onPointerDown={(e) => { e.stopPropagation(); listeners?.onPointerDown?.(e); }}><GripVertical size={14} /></span>
            {def && (
              <Dropdown
                trigger={['click']}
                open={sizeOpen}
                onOpenChange={setSizeOpen}
                dropdownRender={() => <div className="ant-dropdown-menu dw-size-menu"><SizePicker widget={widget} fit={def.fit} onPick={(next) => { onResize(next); setSizeOpen(false); }} /></div>}
              >
                <button type="button" className="dw-tool" title="Size" data-action="resize"><Maximize2 size={13} /></button>
              </Dropdown>
            )}
            <button type="button" className="dw-tool danger" title="Remove" data-action="remove" onClick={onRemove}><X size={14} /></button>
          </div>
          <div className="dw-name-tag">{def?.title || widget.type}</div>
          {def && (
            <>
              <span className="dw-stretch e" data-stretch="e" onPointerDown={stretch('e')} title="Drag to make wider or narrower" />
              <span className="dw-stretch s" data-stretch="s" onPointerDown={stretch('s')} title="Drag to make taller or shorter" />
              <span className="dw-stretch se" data-stretch="se" onPointerDown={stretch('es')} title="Drag to resize" />
            </>
          )}
          {preview && <div className="dw-size-badge">{preview.w} × {preview.h}</div>}
        </>
      )}
    </div>
  );
};

/** A widget in the panel: drag it onto the dashboard, or press Add */
const LibraryItem = ({ def, count, full, onAdd }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `lib:${def.type}`, data: { fromLibrary: true, type: def.type }, disabled: full });
  return (
    <div ref={setNodeRef} className={`dw-library-item${isDragging ? ' dw-lib-dragging' : ''}`} data-library-widget={def.type} {...attributes} {...listeners}>
      <span className="dw-library-grip"><GripVertical size={14} /></span>
      <div className="dw-library-text">
        <div className="dw-library-title">
          {def.title}
          {count ? <span className="dw-library-on">added{count > 1 ? ` ×${count}` : ''}</span> : null}
        </div>
        <div className="dw-library-desc">{def.description}</div>
      </div>
      <Button size="small" icon={<PlusOutlined />} disabled={full} onClick={() => onAdd(def.type)} data-add={def.type}>Add</Button>
    </div>
  );
};

/** The widget panel docked beside the grid while customizing: everything this user can add */
const WidgetLibrary = ({ access, dashboard, onAdd, onClose }) => {
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const counts = useMemo(() => (dashboard?.widgets || []).reduce((acc, wd) => ({ ...acc, [wd.type]: (acc[wd.type] || 0) + 1 }), {}), [dashboard]);
  const available = WIDGETS.filter((wd) => wd.available(access));
  const categories = CATEGORIES.filter((c) => available.some((wd) => wd.category === c.key));
  const term = search.trim().toLowerCase();
  const shown = available.filter((wd) => (category === 'all' || wd.category === category)
    && (!term || wd.title.toLowerCase().includes(term) || wd.description.toLowerCase().includes(term)));
  const full = (dashboard?.widgets || []).length >= MAX_WIDGETS;

  return (
    <aside className="dw-library" data-widget-library>
      <div className="dw-library-head">
        <div>
          <div className="dw-library-heading">Widgets</div>
          <div className="dw-library-sub">Drag one onto the dashboard, or press Add</div>
        </div>
        <button type="button" className="dw-tool" title="Close" onClick={onClose} data-close-library><X size={15} /></button>
      </div>
      <Input allowClear prefix={<SearchOutlined />} placeholder="Search widgets" value={search} onChange={(e) => setSearch(e.target.value)} className="generic-input-control" data-library-search />
      <div className="dw-chips">
        <Tag.CheckableTag checked={category === 'all'} onChange={() => setCategory('all')}>All</Tag.CheckableTag>
        {categories.map((c) => <Tag.CheckableTag key={c.key} checked={category === c.key} onChange={() => setCategory(c.key)}>{c.label}</Tag.CheckableTag>)}
      </div>
      {full && <div className="dw-library-note">This dashboard has {MAX_WIDGETS} widgets, the most it can hold. Remove one to add another.</div>}
      <div className="dw-library-list">
        {shown.length === 0 && <Empty description="No widgets match." image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        {shown.map((wd) => <LibraryItem key={wd.type} def={wd} count={counts[wd.type]} full={full} onAdd={onAdd} />)}
      </div>
    </aside>
  );
};

/** New dashboard (blank or from a ready-made one), rename, or duplicate */
const NameModal = ({ mode, initial, access, close, onSubmit }) => {
  const [name, setName] = useState(initial?.name || '');
  const [template, setTemplate] = useState('blank');
  const templates = availableTemplates(access);
  const title = mode === 'rename' ? 'Rename dashboard' : mode === 'duplicate' ? 'Duplicate dashboard' : 'New dashboard';
  const clean = name.replace(/\s+/g, ' ').trim();
  const submit = () => clean && onSubmit({ name: clean.slice(0, 60), template });
  return (
    <ModalShell open close={close} title={title} footer={<ModalFooter okText={mode === 'rename' ? 'Save' : 'Create'} onOk={submit} disabled={!clean} close={close} />}>
      <div className="dw-form">
        <label className="dw-label" htmlFor="dw-name">Name</label>
        <Input id="dw-name" autoFocus maxLength={60} className="generic-input-control" value={name} placeholder="e.g. Finance, Night shift, My sites" onChange={(e) => setName(e.target.value)} onPressEnter={submit} data-dashboard-name />
        {mode === 'new' && (
          <>
            <div className="dw-label" style={{ marginTop: 16 }}>Start from</div>
            <div className="dw-templates">
              {[{ key: 'blank', name: 'Blank', description: 'An empty dashboard - add the widgets you want.' }, ...templates].map((t) => (
                <button
                  type="button"
                  key={t.key}
                  data-template={t.key}
                  className={`dw-template${template === t.key ? ' on' : ''}`}
                  onClick={() => { setTemplate(t.key); if (!name || TEMPLATES.some((x) => x.name === name)) setName(t.key === 'blank' ? '' : t.name); }}
                >
                  <b>{t.name}</b>
                  <span>{t.description}</span>
                  {t.key !== 'blank' && <em>{templateWidgets(t, access).length} widgets · yours to change</em>}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
};

// What each action icon in the dashboard list does - shown on hover, one plain sentence
const ACTIONS = {
  customize: { icon: <EditOutlined />, title: 'Customize', hint: 'Add widgets, drag them around, stretch or shrink them, or remove them.' },
  rename: { icon: <FormOutlined />, title: 'Rename', hint: 'Give this dashboard a new name.' },
  duplicate: { icon: <CopyOutlined />, title: 'Duplicate', hint: 'Make a second copy you can change separately.' },
  reset: { icon: <ReloadOutlined />, title: 'Reset', hint: 'Put back the widgets this dashboard started with.' },
  delete: { icon: <DeleteOutlined />, title: 'Delete', hint: 'Remove this dashboard. Your other dashboards stay as they are.' },
  copy: { icon: <CopyOutlined />, title: 'Make an editable copy', hint: 'The standard dashboards can\'t change - this starts your own with the same widgets, to change however you like.' },
};

/** One action icon on a dashboard's row in the list; the click acts on that row's dashboard, not opening it */
const RowAction = ({ kind, onClick, disabled }) => (
  // shown inside the list panel so it sits right above the icon
  <Tooltip placement="top" mouseEnterDelay={0.15} getPopupContainer={(node) => node.closest('.dw-picker-panel') || document.body} overlayClassName="dw-row-tip" title={<span><b>{ACTIONS[kind].title}</b><br />{ACTIONS[kind].hint}</span>}>
    <button
      type="button"
      className={`dw-row-btn${kind === 'delete' ? ' danger' : ''}`}
      disabled={disabled}
      aria-label={ACTIONS[kind].title}
      data-row-action={kind}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (!disabled) onClick(); }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {ACTIONS[kind].icon}
    </button>
  </Tooltip>
);

/**
 * The one control above the dashboard (HubSpot style): the current dashboard's name as a title. It
 * opens a list with search - the standard dashboards (as they've always been), then the user's own -
 * each with its name on the left and its action icons on the right (hover one to see what it does), and Create fixed
 * at the bottom. Only while customizing do Done / Widgets buttons show beside it.
 */
export const DashboardBar = ({ standard, store, activeKey, onSelect, access, editing, setEditing, libraryOpen, setLibraryOpen }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const { dashboards, status, commit, updateDashboard } = store;
  const custom = dashboards.find((d) => d.id === activeKey);
  const standardActive = !custom && (standard.find((s) => s.key === activeKey) || standard[0]);
  const label = custom ? custom.name : standardActive?.label;
  const currentKey = custom ? custom.id : standardActive?.key;
  const canCreate = dashboards.length < MAX_DASHBOARDS;
  const templateOf = (key) => TEMPLATES.find((t) => t.key === key);

  const close = () => { setOpen(false); setSearch(''); };
  const pick = (key) => { setEditing(false); setLibraryOpen(false); onSelect(key); };
  const act = (fn) => () => { close(); fn(); };

  const createDashboard = ({ name, template: chosen }) => {
    const source = templateOf(chosen);
    const dashboard = { id: newId('d'), name, template: source ? source.key : null, widgets: source ? templateWidgets(source, access) : [] };
    commit([...dashboards, dashboard]);
    onSelect(dashboard.id);
    setModal(null);
    // a blank one opens ready to fill
    setEditing(!source);
    setLibraryOpen(!source);
  };

  // a standard dashboard can't change, but its widgets can start one of your own
  const copyStandard = (std) => {
    const source = templateOf(std.key.toLowerCase());
    const dashboard = { id: newId('d'), name: `My ${std.label.replace(/ Dashboard$/, '')}`, template: source.key, widgets: templateWidgets(source, access) };
    commit([...dashboards, dashboard]);
    onSelect(dashboard.id);
    setEditing(true);
    setLibraryOpen(true);
  };

  const customize = (d) => { onSelect(d.id); setEditing(true); setLibraryOpen(true); };

  const duplicateDashboard = (source, { name }) => {
    const dashboard = { ...source, id: newId('d'), name, widgets: source.widgets.map((wd) => ({ ...wd, id: newId('w') })) };
    commit([...dashboards, dashboard]);
    onSelect(dashboard.id);
    setModal(null);
  };

  const resetDashboard = (d) => {
    const before = dashboards;
    updateDashboard(d.id, { widgets: templateWidgets(templateOf(d.template), access) });
    message.open({ key: 'dw-reset', type: 'info', duration: 5, content: <span>{d.name} reset <button type="button" className="dw-undo" onClick={() => { commit(before); message.destroy('dw-reset'); }}>Undo</button></span> });
  };

  const deleteDashboard = () => {
    const target = deleting;
    commit(dashboards.filter((d) => d.id !== target.id));
    setDeleting(null);
    if (target.id === currentKey) { setEditing(false); onSelect(standard[0].key); }
  };

  const term = search.trim().toLowerCase();
  const match = (name) => !term || name.toLowerCase().includes(term);
  const row = (key, name, actions) => ({
    key,
    label: (
      <span className="dw-pick-item" data-dashboard-row={name}>
        <span className="dw-pick-name">{name}</span>
        {key === currentKey && <CheckOutlined className="dw-pick-check" />}
        <span className="dw-row-actions">{actions}</span>
      </span>
    ),
  });
  const standardShown = standard.filter((s) => match(s.label));
  const mineShown = dashboards.filter((d) => match(d.name));
  const items = [
    standardShown.length > 0 && {
      type: 'group',
      label: 'Standard dashboards',
      children: standardShown.map((std) => row(std.key, std.label, (
        <RowAction kind="copy" disabled={!canCreate || !templateOf(std.key.toLowerCase())} onClick={act(() => copyStandard(std))} />
      ))),
    },
    mineShown.length > 0 && {
      type: 'group',
      label: 'My dashboards',
      children: mineShown.map((d) => row(d.id, d.name, (
        <>
          <RowAction kind="customize" onClick={act(() => customize(d))} />
          <RowAction kind="rename" onClick={act(() => setModal({ mode: 'rename', id: d.id }))} />
          <RowAction kind="duplicate" disabled={!canCreate} onClick={act(() => setModal({ mode: 'duplicate', id: d.id }))} />
          {templateOf(d.template) && <RowAction kind="reset" onClick={act(() => resetDashboard(d))} />}
          <RowAction kind="delete" onClick={act(() => setDeleting(d))} />
        </>
      ))),
    },
  ].filter(Boolean);

  const onMenu = ({ key }) => {
    close();
    pick(key);
  };

  const target = modal?.id ? dashboards.find((d) => d.id === modal.id) : null;

  return (
    <div className={`dw-bar${editing ? ' dw-bar-editing' : ''}`} >
      <Dropdown
        trigger={['click']}
        open={open}
        style={{height:'400px'}}
        onOpenChange={(next) => { setOpen(next); if (!next) setSearch(''); }}
        menu={{ items, selectable: true, selectedKeys: [currentKey], onClick: onMenu }}
        placement="bottomLeft"
        dropdownRender={(menu) => (
          <div className="dw-picker-panel" data-dashboard-picker>
            <div className="dw-picker-search">
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="Search dashboards"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                // typing stays in the box; Escape still closes the panel
                onKeyDown={(e) => { if (e.key === 'Escape') close(); else e.stopPropagation(); }}
                data-picker-search
              />
            </div>
            {/* only the list scrolls; search stays at the top and Create at the bottom */}
            <div className="dw-picker-scroll">
              {standardShown.length + mineShown.length === 0 && term ? <div className="dw-picker-empty">No dashboards match “{search}”.</div> : menu}
            </div>
            <div className="dw-picker-foot">
              <button type="button" className="dw-picker-create" disabled={!canCreate} onClick={act(() => setModal({ mode: 'new' }))} data-picker-create>
                <PlusOutlined /> Create dashboard
              </button>
            </div>
          </div>
        )}
      >
        <button type="button" className="dw-picker" data-dashboard-switch aria-haspopup="menu">
          <span className="dw-picker-kicker">Dashboard</span>
          <span className="dw-picker-title"><span className="dw-switch-label">{label}</span><DownOutlined className="dw-picker-caret" /></span>
        </button>
      </Dropdown>

      <div className="dw-bar-right">
      {custom && status === 'saving' && <span className="dw-status">Saving…</span>}
      {custom && status === 'saved' && <span className="dw-status ok" data-saved><CheckOutlined /> All changes saved</span>}
      {custom && status === 'error' && <span className="dw-status err">Couldn't save - check your connection</span>}
      {editing && (
        <div className="dw-actions-bar">
          {!libraryOpen && <button type="button" className="hs-btn hs-tertiary" onClick={() => setLibraryOpen(true)} data-add-widgets><AppstoreOutlined /> Widgets</button>}
          <button type="button" className="hs-btn hs-primary" onClick={() => { setEditing(false); setLibraryOpen(false); }} data-customize><CheckOutlined /> Done</button>
        </div>
      )}
      </div>

      {modal && (
        <NameModal
          mode={modal.mode}
          initial={modal.mode === 'rename' ? target : modal.mode === 'duplicate' ? { name: `${target?.name} copy` } : null}
          access={access}
          close={() => setModal(null)}
          onSubmit={(values) => {
            if (modal.mode === 'new') createDashboard(values);
            else if (modal.mode === 'duplicate') duplicateDashboard(target, values);
            else { updateDashboard(target.id, { name: values.name }); setModal(null); }
          }}
        />
      )}

      {deleting && (
        <ModalShell open close={() => setDeleting(null)} title="Delete dashboard" footer={<ModalFooter okText="Delete" onOk={deleteDashboard} close={() => setDeleting(null)} />}>
          <p style={{ margin: '0 24px' }}>Delete <b>{deleting.name}</b> and its {deleting.widgets.length} widget{deleting.widgets.length === 1 ? '' : 's'}? Your other dashboards aren't affected.</p>
        </ModalShell>
      )}
    </div>
  );
};

// the grid is also a drop target (a widget dropped past the last one goes at the end); it has to
// sit inside the DndContext, so it's its own component
const DropGrid = ({ className, name, children }) => {
  const { setNodeRef } = useDroppable({ id: GRID_DROP });
  return <div ref={setNodeRef} className={className} data-dashboard={name}>{children}</div>;
};

// Where a dragged widget will land, at its own size. It's a drop target too, so the pointer resting
// on it keeps the spot (otherwise the grid shifting round it would move it again).
const Placeholder = ({ w, h, title }) => {
  const { setNodeRef } = useDroppable({ id: PLACEHOLDER });
  return (
    <div ref={setNodeRef} className={`dw-cell dw-w-${w} dw-h-${h} dw-placeholder`} data-drop-placeholder={`${w}x${h}`}>
      <span><b>{title}</b>Drops here · {w} × {h}</span>
    </div>
  );
};

/** The grid of one of the user's own dashboards, with the widget panel beside it while customizing */
export const CustomDashboard = ({ dashboard, store, access, editing, setEditing, libraryOpen, setLibraryOpen, renderWidget, readOnly = false }) => {
  const { dashboards, commit, updateDashboard } = store;
  const [flash, setFlash] = useState(null);
  // { kind: 'library' | 'move', type, id, w, h } while something is being dragged
  const [dragging, setDragging] = useState(null);
  // where it would land: a widget's id (in front of it) or the grid itself (at the end)
  const [target, setTarget] = useState(null);
  const targetTimer = useRef(null);
  const flashTimer = useRef(null);
  useEffect(() => () => { clearTimeout(flashTimer.current); clearTimeout(targetTimer.current); }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  // widgets the user can't have (module off, role changed) are hidden - kept, and greyed while customizing
  const widgets = dashboard.widgets.filter((wd) => editing || WIDGET_BY_TYPE[wd.type]?.available(access));
  const setWidgets = (change) => updateDashboard(dashboard.id, (d) => ({ widgets: change(d.widgets) }));

  const showAdded = (widget) => {
    setFlash(widget.id);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 2200);
    setTimeout(() => document.querySelector(`[data-widget-id="${widget.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 150);
  };

  const addWidget = (type, beforeId) => {
    if (dashboard.widgets.length >= MAX_WIDGETS) return;
    const widget = makeWidget(type);
    setWidgets((all) => {
      const at = beforeId ? all.findIndex((wd) => wd.id === beforeId) : -1;
      return at < 0 ? [...all, widget] : [...all.slice(0, at), widget, ...all.slice(at)];
    });
    showAdded(widget);
    message.success({ content: `${WIDGET_BY_TYPE[type].title} added`, key: 'dw-add', duration: 1.5 });
  };

  // what's under the pointer: the drop box first, then a widget, then the grid
  const collision = (args) => {
    const hits = pointerWithin(args);
    return [hits.find((h) => h.id === PLACEHOLDER) || hits.find((h) => h.id !== GRID_DROP) || hits.find((h) => h.id === GRID_DROP)].filter(Boolean);
  };

  const onDragStart = ({ active }) => {
    const data = active.data.current || {};
    if (data.fromLibrary) {
      const def = WIDGET_BY_TYPE[data.type];
      setDragging({ kind: 'library', type: data.type, w: def.size.w, h: def.size.h });
    } else {
      const widget = widgets.find((wd) => wd.id === active.id);
      if (widget) setDragging({ kind: 'move', type: widget.type, id: widget.id, w: widget.w, h: widget.h });
    }
    setTarget(null);
  };
  // the box follows the pointer once it has rested a moment on a new spot (the grid reflowing round
  // the box can't then bounce it back and forth); over the box itself it stays where it is
  const onDragOver = ({ over }) => {
    if (!over || over.id === PLACEHOLDER) return;
    clearTimeout(targetTimer.current);
    targetTimer.current = setTimeout(() => setTarget(over.id), 90);
  };
  const endDrag = () => { clearTimeout(targetTimer.current); setDragging(null); setTarget(null); };
  const onDragEnd = ({ active }) => {
    const where = target;
    const moving = dragging;
    endDrag();
    if (!where || !moving) return;
    if (moving.kind === 'library') {
      addWidget(active.data.current.type, where === GRID_DROP ? null : where);
      return;
    }
    if (where === moving.id) return;
    setWidgets((all) => {
      const item = all.find((wd) => wd.id === moving.id);
      if (!item) return all;
      const rest = all.filter((wd) => wd.id !== moving.id);
      const at = where === GRID_DROP ? rest.length : rest.findIndex((wd) => wd.id === where);
      return at < 0 ? all : [...rest.slice(0, at), item, ...rest.slice(at)];
    });
  };
  // the drop box shows unless the widget would land where it already is
  const showBoxBefore = (id) => !!dragging && target === id && !(dragging.kind === 'move' && dragging.id === id);

  const removeWidget = (widget) => {
    const before = dashboards;
    setWidgets((all) => all.filter((wd) => wd.id !== widget.id));
    message.open({
      key: 'dw-remove',
      type: 'info',
      duration: 5,
      content: (
        <span>
          {WIDGET_BY_TYPE[widget.type]?.title || 'Widget'} removed{' '}
          <button type="button" className="dw-undo" onClick={() => { commit(before); message.destroy('dw-remove'); }}>Undo</button>
        </span>
      ),
    });
  };
  const resizeWidget = (widget, size) => setWidgets((all) => all.map((wd) => (wd.id === widget.id ? { ...wd, ...size } : wd)));
  const setSettings = (widget, settings) => setWidgets((all) => all.map((wd) => (wd.id === widget.id ? { ...wd, settings } : wd)));

  const dragDef = dragging && WIDGET_BY_TYPE[dragging.type];
  const box = dragging ? <Placeholder w={dragging.w} h={dragging.h} title={dragDef?.title} /> : null;

  return (
    <DndContext sensors={sensors} collisionDetection={collision} onDragStart={onDragStart} onDragOver={onDragOver} onDragEnd={onDragEnd} onDragCancel={endDrag}>
      <div className={`dw-root${editing ? ' dw-editing' : ''}${editing && libraryOpen ? ' dw-with-library' : ''}`}>
        <div className="dw-main">
          {editing && <div className="dw-edit-hint">Drag widgets to move them · drag an edge or corner to stretch or shrink · <Maximize2 size={11} /> for columns × rows · <X size={11} /> to remove</div>}
            <DropGrid className={`dw-grid${dragging ? ' dw-receiving' : ''}`} name={dashboard.name}>
              {widgets.map((widget) => {
                const def = WIDGET_BY_TYPE[widget.type];
                const usable = def?.available(access);
                return (
                  <React.Fragment key={widget.id}>
                  {showBoxBefore(widget.id) && box}
                  <WidgetCell
                    key={widget.id}
                    widget={widget}
                    def={usable ? def : null}
                    editing={editing}
                    readOnly={readOnly}
                    onRemove={() => removeWidget(widget)}
                    onResize={(size) => resizeWidget(widget, size)}
                  >
                    <div className={`dw-inner${flash === widget.id ? ' dw-flash' : ''}`}>
                      {usable && renderWidget(def, {
                        widget,
                        settings: widget.settings || {},
                        onSettings: (settings) => setSettings(widget, settings),
                        size: { w: widget.w, h: widget.h },
                        access,
                      })}
                    </div>
                  </WidgetCell>
                  </React.Fragment>
                );
              })}
              {dragging && target === GRID_DROP && box}
              {widgets.length === 0 && (
                <div className="dw-blank" data-empty-dashboard>
                  <Empty description={<span><b>{dashboard.name}</b> has no widgets yet.{editing ? ' Drag one here from the panel.' : ''}</span>} image={Empty.PRESENTED_IMAGE_SIMPLE}>
                    {!editing && <Button type="primary" icon={<AppstoreOutlined />} onClick={() => { setEditing(true); setLibraryOpen(true); }} data-add-first>Add widgets</Button>}
                  </Empty>
                </div>
              )}
            </DropGrid>
        </div>
        {editing && libraryOpen && <WidgetLibrary access={access} dashboard={dashboard} onAdd={(type) => addWidget(type)} onClose={() => setLibraryOpen(false)} />}
      </div>
      <DragOverlay dropAnimation={null}>
        {dragDef ? <div className="dw-drag-ghost" data-drag-ghost><b>{dragDef.title}</b><span>{dragging.w} × {dragging.h} · drop where the box shows</span></div> : null}
      </DragOverlay>
    </DndContext>
  );
};
